import React, { useState } from "react";
import { ShieldAlert, Lock, User, FileText, Send, CheckCircle2, X } from "lucide-react";
import { apiClient } from "@/lib/api-client";
import { toast } from "sonner";

export default function AidCaseDrawer({ aidCase, onClose, onRefresh }) {
  const [noteInput, setNoteInput] = useState("");

  if (!aidCase) return null;

  const handleAddNote = async () => {
    if (!noteInput.trim()) return;
    try {
      await apiClient.post(`/aid-cases/${aidCase._id}/confidential-notes`, { note: noteInput });
      toast.success("Confidential case note logged");
      setNoteInput("");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Failed to log confidential note");
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      await apiClient.patch(`/aid-cases/${aidCase._id}/status`, { status });
      toast.success(`Case status updated to ${status}`);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end">
      <div className="bg-white w-full max-w-xl h-full shadow-2xl p-6 overflow-y-auto space-y-6 flex flex-col justify-between font-sans">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 pb-4">
            <div>
              <span className="text-[11px] font-bold text-amber-600 uppercase tracking-wider flex items-center gap-1">
                <Lock className="w-3.5 h-3.5" /> Confidential Beneficiary Case File
              </span>
              <h2 className="text-xl font-bold text-slate-900 mt-1">{aidCase.applicant_name}</h2>
              <p className="text-xs text-slate-500">{aidCase.applicant_email} • Household of {aidCase.household_size}</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Details */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
            <div className="flex justify-between items-center text-xs">
              <span className="font-semibold text-slate-700">Category:</span>
              <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 font-bold rounded-md">{aidCase.assistance_category}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="font-semibold text-slate-700">Requested Amount:</span>
              <span className="text-sm font-bold text-slate-900 font-mono">${aidCase.requested_amount?.toFixed(2)}</span>
            </div>
            {aidCase.hardship_explanation && (
              <div className="text-xs pt-2 border-t border-slate-200">
                <span className="font-semibold text-slate-700 block mb-1">Hardship Explanation:</span>
                <p className="text-slate-600 leading-relaxed italic">{aidCase.hardship_explanation}</p>
              </div>
            )}
          </div>

          {/* Status buttons */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-700 block">Case Workflow Status</label>
            <div className="flex flex-wrap gap-2">
              {["SUBMITTED", "IN_REVIEW", "APPROVED", "DISBURSED", "DENIED"].map((st) => (
                <button
                  key={st}
                  onClick={() => handleUpdateStatus(st)}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-all border ${
                    aidCase.status === st
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>
          </div>

          {/* Restricted Case Worker Notes Section */}
          <div className="space-y-3 pt-4 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                <ShieldAlert className="w-4 h-4 text-amber-500" />
                Case Worker Confidential Notes (RBAC Guarded)
              </h3>
            </div>

            <div className="space-y-2">
              <textarea
                rows={2}
                placeholder="Log confidential case notes (visible only to case workers)..."
                value={noteInput}
                onChange={(e) => setNoteInput(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs"
              />
              <button
                onClick={handleAddNote}
                className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5"
              >
                <Send className="w-3.5 h-3.5" /> Save Confidential Note
              </button>
            </div>

            <div className="space-y-2.5 max-h-48 overflow-y-auto pt-2">
              {aidCase.confidential_case_notes?.length > 0 ? (
                aidCase.confidential_case_notes.map((n, idx) => (
                  <div key={idx} className="bg-amber-50/50 p-3 rounded-lg border border-amber-200/70 text-xs space-y-1">
                    <div className="flex justify-between text-[10px] text-amber-800 font-semibold">
                      <span>{n.author}</span>
                      <span>{new Date(n.timestamp).toLocaleDateString()}</span>
                    </div>
                    <p className="text-slate-700">{n.note}</p>
                  </div>
                ))
              ) : (
                <div className="text-xs text-slate-400 italic">No confidential notes logged yet.</div>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl"
        >
          Close Case File
        </button>
      </div>
    </div>
  );
}
